/**
 * The arithmetic of a browser-to-blob block upload: how a file splits into blocks, what each block is called,
 * and the two Azure Blob REST addresses it is sent to. No network here, so it is tested on its own.
 */

/** Each block is 8 MB: a 5 GB file is 640 blocks, far inside Azure's 50,000-block limit. */
export const BLOCK_SIZE_BYTES = 8 * 1024 * 1024

/** Blocks sent at once. More barely helps a browser's upload bandwidth and starves the rest of the page. */
export const PARALLEL_BLOCKS = 4

/** Azure needs every block id in one blob to be the same length, so the index is zero-padded before encoding. */
const BLOCK_INDEX_DIGITS = 6

export interface PlannedBlock {
    index: number
    /** Inclusive byte offset. */
    start: number
    /** Exclusive byte offset. */
    end: number
    /** Base64 of `block-{index:000000}`; the same length for every block. */
    id: string
}

export function blockId(index: number): string {
    return btoa(`block-${String(index).padStart(BLOCK_INDEX_DIGITS, '0')}`)
}

/** The blocks a file of `totalBytes` uploads as, in order. The last one carries the remainder. */
export function planBlocks(totalBytes: number, blockSize: number = BLOCK_SIZE_BYTES): PlannedBlock[] {
    if (!Number.isInteger(totalBytes) || totalBytes < 0) throw new Error(`Cannot plan blocks for ${totalBytes} bytes`)
    if (!Number.isInteger(blockSize) || blockSize <= 0) throw new Error(`Cannot plan blocks of ${blockSize} bytes`)
    const count = Math.ceil(totalBytes / blockSize)
    if (count >= 10 ** BLOCK_INDEX_DIGITS) throw new Error(`A ${totalBytes}-byte file needs more blocks than ids allow`)
    return Array.from({ length: count }, (_, index) => ({
        index,
        start: index * blockSize,
        end: Math.min(totalBytes, (index + 1) * blockSize),
        id: blockId(index),
    }))
}

function withQuery(uploadUrl: string, params: Record<string, string>): string {
    const url = new URL(uploadUrl)
    for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value)
    return url.toString()
}

/** Put Block: the SAS URL with `comp=block` and the block's id, URL-encoded by URLSearchParams. */
export function blockUrl(uploadUrl: string, id: string): string {
    return withQuery(uploadUrl, { comp: 'block', blockid: id })
}

/** Put Block List: commits the blocks, in the order given, as the blob. */
export function blockListUrl(uploadUrl: string): string {
    return withQuery(uploadUrl, { comp: 'blocklist' })
}

export function blockListXml(ids: readonly string[]): string {
    return `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids.map(id => `<Latest>${id}</Latest>`).join('')}</BlockList>`
}

export interface TransferRate {
    bytesPerSecond: number | null
    secondsLeft: number | null
}

/** Seconds of upload before a rate is worth stating: the first block's time says little about the rest. */
const MIN_RATE_SAMPLE_MS = 1000

/** The average rate since the upload began, and how long the rest takes at that rate. Null until it means something. */
export function transferRate(uploadedBytes: number, totalBytes: number, elapsedMs: number): TransferRate {
    if (elapsedMs < MIN_RATE_SAMPLE_MS || uploadedBytes <= 0) return { bytesPerSecond: null, secondsLeft: null }
    const bytesPerSecond = uploadedBytes / (elapsedMs / 1000)
    return { bytesPerSecond, secondsLeft: Math.max(0, totalBytes - uploadedBytes) / bytesPerSecond }
}
